import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Card,
  Typography,
  Space,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Pricing scheme types
const SCHEME_TYPES = [
  { value: 'fixed', label: 'Fixed Fee', color: 'blue' },
  { value: 'mdr', label: 'MDR (Percentage)', color: 'green' },
  { value: 'revenue-sharing', label: 'Revenue Sharing', color: 'orange' },
  { value: 'tier', label: 'Tiered Pricing', color: 'purple' },
  { value: 'special', label: 'Special/Hybrid', color: 'red' },
];

const PricingSchemesPage = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const queryClient = useQueryClient();

  // Fetch pricing schemes
  const { data: schemes, isLoading } = useQuery(
    'admin-pricing-schemes',
    async () => {
      const response = await apiClient.get('/api/admin/pricing-schemes');
      return response.data.data.schemes;
    }
  );

  // Create pricing scheme mutation
  const createSchemeMutation = useMutation(
    async (schemeData) => {
      const response = await apiClient.post('/api/admin/pricing-schemes', schemeData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Pricing scheme created successfully');
        queryClient.invalidateQueries('admin-pricing-schemes');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create pricing scheme');
      },
    }
  );

  // Update pricing scheme mutation
  const updateSchemeMutation = useMutation(
    async ({ id, ...schemeData }) => {
      const response = await apiClient.put(`/api/admin/pricing-schemes/${id}`, schemeData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Pricing scheme updated successfully');
        queryClient.invalidateQueries('admin-pricing-schemes');
        setIsModalVisible(false);
        setEditingScheme(null);
        form.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update pricing scheme');
      },
    }
  );

  // Delete pricing scheme mutation
  const deleteSchemeMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/api/admin/pricing-schemes/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Pricing scheme deleted successfully');
        queryClient.invalidateQueries('admin-pricing-schemes');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete pricing scheme');
      },
    }
  );

  const handleCreate = () => {
    setEditingScheme(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (scheme) => {
    setEditingScheme(scheme);
    form.setFieldsValue(scheme);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteSchemeMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingScheme) {
        updateSchemeMutation.mutate({ id: editingScheme.id, ...values });
      } else {
        createSchemeMutation.mutate(values);
      }
    } catch (error) {
      // Silently handle form validation error
    }
  };

  const getSchemeColor = (scheme) => {
    const schemeType = SCHEME_TYPES.find(type => type.value === scheme);
    return schemeType?.color || 'default';
  };

  const columns = [
    {
      title: 'Scheme Type',
      dataIndex: 'scheme',
      key: 'scheme',
      render: (scheme) => {
        const schemeType = SCHEME_TYPES.find(type => type.value === scheme);
        return (
          <Tag color={getSchemeColor(scheme)} icon={<AppstoreOutlined />}>
            {schemeType?.label || scheme}
          </Tag>
        );
      },
      filters: SCHEME_TYPES.map(type => ({ text: type.label, value: type.value })),
      onFilter: (value, record) => record.scheme === value,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Tag color="blue">{code}</Tag>,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => description || <span style={{ color: '#999' }}>No description</span>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this pricing scheme?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0 }}>Pricing Schemes Management</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Pricing Scheme
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={schemes}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} pricing schemes`,
          }}
        />
      </Card>

      <Modal
        title={editingScheme ? 'Edit Pricing Scheme' : 'Add Pricing Scheme'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingScheme(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
        confirmLoading={createSchemeMutation.isLoading || updateSchemeMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="scheme"
            label="Scheme Type"
            rules={[{ required: true, message: 'Please select a scheme type' }]}
          >
            <Select placeholder="Select scheme type">
              {SCHEME_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  <Tag color={type.color} style={{ marginRight: 8 }}>
                    {type.label}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="code"
            label="Scheme Code"
            rules={[
              { required: true, message: 'Please enter scheme code' },
              { pattern: /^[a-z0-9-]+$/, message: 'Code must contain only lowercase letters, numbers, and hyphens' }
            ]}
          >
            <Input
              placeholder="fixed-1"
              disabled={!!editingScheme}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Description of the pricing scheme..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PricingSchemesPage;
